/**
 * The SHARED, non-wizard onboarding controls (RO-2-07, docs/rollover/inventory/
 * actions.md "Domain: welcome" — the 5 `src/onboarding/actions.ts` rows),
 * converted to contracted Hono endpoints. Source:
 * `excellent/src/onboarding/actions.ts`.
 *
 * Distinct from `./welcome.ts` (the five-station arc) and from
 * `./onboarding.ts` (the `GET /api/onboarding` state read) by PROVENANCE, which
 * is why it is its own file: the legacy 15-step wizard's per-step actions were
 * DELETED with the wizard in Wave D, and what remains in that source file are
 * the actions with live consumers OUTSIDE onboarding — the telemetry preference
 * controls (Settings → Usage analytics) and the Getting-Started controls (the
 * home dashboard). Demo-data controls were retired with the verification
 * authority cutover; installed schema columns remain additive-only history.
 *
 * Paths sit under `/api/onboarding/*` alongside the already-ported
 * `GET /api/onboarding` and `POST /api/onboarding/refresh-profile`
 * (`server/routes/tail.ts`). All paths are literal segments with no
 * `:param`, so they cannot shadow those (#44370).
 */

import { z } from "zod";
import type { Operation } from "../types";

// ---------------------------------------------------------------------------
// setTelemetryOptInAction (actions.ts:22) — the post-onboarding control for the
// vendor-telemetry preference (Settings → Usage analytics). Stamps the consent
// INSTANT so the shipper egresses only events created at/after it; pre-consent
// funnel rows stay on-device. Cleared on opt-out so a re-opt-in re-stamps.

export const telemetryOptInBody = z.object({ optIn: z.boolean() });
export type TelemetryOptInBody = z.infer<typeof telemetryOptInBody>;

export const telemetryOptInResponse = z.object({
  ok: z.literal(true),
  optIn: z.boolean(),
  /** the consent instant, or null on opt-out (the from-consent-only bound) */
  optInAt: z.string().nullable(),
});

export const onboardingSetTelemetryOptIn: Operation<
  void,
  void,
  TelemetryOptInBody,
  z.infer<typeof telemetryOptInResponse>
> = {
  method: "POST",
  path: () => "/api/onboarding/telemetry/opt-in",
  body: telemetryOptInBody,
  response: telemetryOptInResponse,
};

// ---------------------------------------------------------------------------
// setTelemetryPublicOptInAction (actions.ts:37) — the SEPARATE, affirmative
// (default-OFF) consent to be included in the PUBLIC open metrics. Distinct
// from the base vendor-telemetry opt-in above; lives in the settings KV (no
// migration) and the flush job enforces it when the sink publishes openly.

export const telemetryPublicOptInBody = z.object({ optIn: z.boolean() });
export type TelemetryPublicOptInBody = z.infer<typeof telemetryPublicOptInBody>;

export const telemetryPublicOptInResponse = z.object({
  ok: z.literal(true),
  optIn: z.boolean(),
});

export const onboardingSetTelemetryPublicOptIn: Operation<
  void,
  void,
  TelemetryPublicOptInBody,
  z.infer<typeof telemetryPublicOptInResponse>
> = {
  method: "POST",
  path: () => "/api/onboarding/telemetry/public-opt-in",
  body: telemetryPublicOptInBody,
  response: telemetryPublicOptInResponse,
};

// ---------------------------------------------------------------------------
// dismissGettingStartedCard (actions.ts:43) — dismiss the whole home
// Getting-Started card.

export const checklistDismissResponse = z.object({
  ok: z.literal(true),
  dismissedAt: z.string(),
});

export const onboardingDismissChecklist: Operation<
  void,
  void,
  void,
  z.infer<typeof checklistDismissResponse>
> = {
  method: "POST",
  path: () => "/api/onboarding/checklist/dismiss",
  response: checklistDismissResponse,
};

// ---------------------------------------------------------------------------
// dismissGettingStartedItem (actions.ts:51) — dismiss ONE checklist item.
//
// The source took `FormData` (a progressively-enhanced `<form action>`); the
// endpoint takes JSON. The blank-key no-op is preserved: the source returns
// early on an empty `item`, so `item` is `.min(1)` here ONLY to reject a value
// that could never have done anything — the caller sends a real key or does not
// call. `dismissed` echoes the merged set so the caller need not re-read.

export const checklistDismissItemBody = z.object({ item: z.string().min(1) });
export type ChecklistDismissItemBody = z.infer<typeof checklistDismissItemBody>;

export const checklistDismissItemResponse = z.object({
  ok: z.literal(true),
  /** the full merged dismissed-item set after this call (idempotent) */
  dismissed: z.array(z.string()),
});

export const onboardingDismissChecklistItem: Operation<
  void,
  void,
  ChecklistDismissItemBody,
  z.infer<typeof checklistDismissItemResponse>
> = {
  method: "POST",
  path: () => "/api/onboarding/checklist/dismiss-item",
  body: checklistDismissItemBody,
  response: checklistDismissItemResponse,
};

export const onboardingControls = {
  setTelemetryOptIn: onboardingSetTelemetryOptIn,
  setTelemetryPublicOptIn: onboardingSetTelemetryPublicOptIn,
  dismissChecklist: onboardingDismissChecklist,
  dismissChecklistItem: onboardingDismissChecklistItem,
};
